import * as React from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@material-ui/core/Box';
import { useSelector } from 'react-redux';
import { useInView } from 'react-intersection-observer';
import clsx from 'clsx';

import { DateFormPanel } from 'components/DateFormPanel/DateFormPanel';
import { ProductDetailsViewProps } from 'lib/themes/themes';

import { toProductShape } from 'lib/util/productShape';

import { HighlightsView } from './HighlightsView';
import { OperationInfoView } from './OperationInfoView';
import { PromotionDetailsView } from './PromotionDetailsView';
import { ProductOutlineView } from './ProductOutlineView';
import { ProductServicesView } from './ProductServicesView';
import { ProductInfoView } from './ProductInfoView';
import { AllPicturesView } from './AllPicturesView';
import baseStyles from '../base.module.css';
import styles from './ProductDetailsView.module.css';
import { ProductReviewsPane } from 'components/ProductReviewsPane/ProductReviewsPane';
import { CurrentUrlContext } from 'contexts/CurrentUrlContext';
import { ShareButtons } from 'components/Sns/ShareButtons';
import { ProductDetailsBookmarkButton } from 'components/ProductDetailsBookmarkButton/ProductDetailsBookmarkButton';
import {
  selectIsPMP,
  selectPopularProductIds,
  selectRecommendedProductIds,
} from 'lib/util/privateMarketplace';
import { useCurrencyFormatter } from 'hooks/useCurrencyFormatter';

const ProductDetailsView = ({
  product: apiProduct,
  shouldShowBookNowButton,
  features,
}: ProductDetailsViewProps) => {
  const { t, i18n } = useTranslation();
  const [showAllPictures, setShowAllPictures] = React.useState<boolean>(false);
  const [calendarRef, calendarInView] = useInView();

  const currencyFormatter = useCurrencyFormatter();

  const isPMP = useSelector(selectIsPMP);
  const { url } = React.useContext(CurrentUrlContext);

  const product = toProductShape(apiProduct);

  const popularProductIds = useSelector(selectPopularProductIds);
  const ranking = popularProductIds.indexOf(product.id) + 1;
  const isPopular = !!ranking;

  const recommendedProductIds = useSelector(selectRecommendedProductIds);
  const isRecommended = recommendedProductIds.includes(product.id);

  let discountText = '';
  if (product.bestDiscountPercent || product.bestDiscountGross) {
    discountText = t('{{discount}} OFF', {
      discount: product.bestDiscountPercent
        ? `${product.bestDiscountPercent}%`
        : currencyFormatter.format(product.bestDiscountGross),
    });
  }

  const showReviewCount = product.reviewCount != null && product.reviewCount !== 0;

  return (
    <main className={baseStyles['base-main']}>
      <div className={clsx(baseStyles['base-inner'], baseStyles['base-singleTop'])}>
        <div className={styles['maininfo']}>
          <h1 className={clsx(styles['maininfo__ttl'], 'base-key__text')}>{product.name}</h1>
          {features.length > 0 && (
            <Box display="flex" justifyContent="flex-start" mb={2}>
              <div
                className={clsx(styles['subheader__feature'], 'maincolor-border', 'maincolor-txt')}
              >
                {features[0]}
              </div>
            </Box>
          )}
          {(isPopular || isRecommended || discountText || isPMP || showReviewCount) && (
            <Box display="flex">
              <div className={styles['headerinfo']}>
                {(isPopular || isRecommended || discountText) && (
                  <div className="base-single__headerinfo__labels">
                    {isPopular && (
                      <div className="base-single__headerinfo__labels__item ranking">
                        <img src="/static/images/ic_ranking_yellow.svg" alt="ranking" />
                        {t('High Ranking')}
                      </div>
                    )}
                    {isRecommended && (
                      <div className="base-single__headerinfo__labels__item recommend">
                        <img src="/static/images/ic_recommend_green.svg" alt="recommended" />
                        {t('Recommended')}
                      </div>
                    )}
                    {discountText && (
                      <div className="base-single__headerinfo__labels__item promotion">
                        <img src="/static/images/ic_special.svg" alt="special" />
                        {discountText}
                      </div>
                    )}
                  </div>
                )}
                <div className="base-single__headerinfo__other">
                  {showReviewCount && (
                    <div className="base-single__headerinfo__other__review">
                      <a href="#reviews">
                        <img src="/static/images/ic_star_on.svg" alt="star" />
                        <span>{(product.reviewRating ?? 0).toFixed(1)}</span>
                        <span>({product.reviewCount})</span>
                      </a>
                    </div>
                  )}
                  {isPMP && <ProductDetailsBookmarkButton productId={product.id} />}
                </div>
              </div>
            </Box>
          )}
          <div className={styles['product__subheader']}>
            <div className={styles['maininfo__tag']}>
              {product.tags.map((tag) => (
                <p
                  key={tag}
                  className={clsx(baseStyles['color-sub--txt'], baseStyles['color-sub--bor'])}
                >
                  {tag}
                </p>
              ))}
            </div>
            <div className={styles['sns__buttons__container']}>
              <ShareButtons url={url} productName={product?.name || ''} locale={i18n.language} />
            </div>
          </div>
        </div>
        <div className={styles['pictures']} style={isPMP ? { marginTop: 0 } : undefined}>
          <div className={styles['pictures__main']}>
            <ul className={styles['pictures__main__photo']}>
              {product.mediaURLs?.slice(0, 5).map((url) => (
                <li key={url.url}>
                  {url.type === 'IMAGE' ? (
                    <img src={url.url} />
                  ) : (
                    <iframe
                      width="100%"
                      height="100%"
                      src={`${url.url}?enablejsapi=1`}
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen={true}
                    />
                  )}
                </li>
              ))}
            </ul>
            <a
              className={clsx(
                styles['pictures__main__more'],
                baseStyles['color-bor--black'],
                baseStyles['color-txt']
              )}
              onClick={() => setShowAllPictures(true)}
            >
              <div className={styles['pictures__main__more__ic']}>
                <span className={baseStyles['color-bg--black']}></span>
                <span className={baseStyles['color-bg--black']}></span>
                <span className={baseStyles['color-bg--black']}></span>
                <span className={baseStyles['color-bg--black']}></span>
              </div>
              <p>{t('See All Photos')}</p>
            </a>
          </div>
        </div>
        <AllPicturesView
          urls={product.mediaURLs}
          open={showAllPictures}
          onClose={() => setShowAllPictures(false)}
        />
      </div>

      <div className={clsx(baseStyles['base-inner'], baseStyles['base-column'])}>
        <div className={baseStyles['base-column__main']}>
          <OperationInfoView product={product} />
          <PromotionDetailsView product={product} />
          <HighlightsView product={product} />
          <ProductOutlineView product={product} />
          <ProductServicesView product={product} />

          <ProductInfoView product={product} />

          <ProductReviewsPane product={apiProduct} />
        </div>

        <div
          className={clsx(baseStyles['base-column__calendar'], 'scroll-target-section')}
          ref={calendarRef}
          id="booking-calendar"
        >
          <span className={baseStyles['color-sub--bg']} style={{ opacity: 0.08 }}></span>
          <DateFormPanel isEmbedded={false} showPrices={true} />
        </div>
        {shouldShowBookNowButton && !calendarInView && (
          <div className={baseStyles['base-column__spFixed']}>
            <a
              className={clsx(
                baseStyles['base-column__spFixed__btn'],
                baseStyles['color-main--bg'],
                baseStyles['color-txt--white']
              )}
              href="#booking-calendar"
            >
              {t('Book')}
            </a>
            <span
              className={clsx(
                baseStyles['base-column__spFixed__bg1'],
                baseStyles['color-gradient']
              )}
            ></span>
            <span
              className={clsx(baseStyles['base-column__spFixed__bg2'], baseStyles['color-bg'])}
            ></span>
          </div>
        )}
      </div>
    </main>
  );
};

export default ProductDetailsView;
